import { GraduationCap } from 'lucide-react-native';
import { useEffect, useState } from 'react';

import { Input } from '@/components/Input';
import { PickerField } from '@/components/PickerField';
import { schoolsService } from '@/services/schools';

const OTHER_FALLBACK = 'Other';

/**
 * School picker over the fixed D1 catalog, with the same "pick one or Other"
 * shape as the Typeform athlete form. Picking Other reveals a text field and
 * we store whatever they type — D2/D3/NAIA/JUCO athletes are a real part of
 * the base and the catalog is D1 only.
 *
 * `value` is the stored school name, which is either a catalog entry or that
 * free text; there is no separate column and no id.
 *
 * Returns a fragment on purpose: wrapping PickerField in a View would trap its
 * open-state zIndex inside that wrapper, and on iOS zIndex only reorders
 * within a single parent — the next sibling field would paint over the
 * dropdown.
 */
export function SchoolPicker({
  label = 'School',
  value,
  onSelect,
}: {
  label?: string;
  value: string | null;
  onSelect: (school: string | null) => void;
}) {
  const [schools, setSchools] = useState<string[]>([]);
  const [other, setOther] = useState(OTHER_FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    schoolsService
      .fetch()
      .then(({ schools: list, other: otherLabel }) => {
        if (cancelled) return;
        setSchools(list);
        setOther(otherLabel);
        setLoaded(true);
      })
      .catch(() => {
        // Catalog unreachable. Fall through to the Other branch rather than
        // showing an empty dropdown the athlete can't get past.
        if (!cancelled) {
          setLoaded(true);
          setUnreachable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived, not latched at fetch time: a value can arrive after mount (an
  // authStore refresh, a prefill landing late) and a stale `isOther` would
  // either hide the free-text field holding their school or show the school
  // in both fields at once.
  //
  // `null` means Other is selected but nothing typed yet, so it must not flip
  // the branch back — that would close the field mid-answer.
  const [otherSelected, setOtherSelected] = useState(false);
  const isOther =
    unreachable || otherSelected || (loaded && value !== null && !schools.includes(value));

  function handlePick(picked: string | null) {
    if (picked === other) {
      setOtherSelected(true);
      // Nothing is chosen yet — the free-text field below is the real answer.
      onSelect(null);
      return;
    }
    setOtherSelected(false);
    onSelect(picked);
  }

  return (
    <>
      <PickerField
        label={label}
        icon={GraduationCap}
        options={schools}
        pinned={loaded ? other : undefined}
        value={isOther ? other : value}
        onSelect={handlePick}
        placeholder="Pick or type your school"
        emptyLabel="Loading schools…"
      />
      {isOther && (
        <Input
          label="Which school?"
          value={value ?? ''}
          onChangeText={(t) => onSelect(t.trim() ? t : null)}
          placeholder="Type your school"
          autoCapitalize="words"
          autoCorrect={false}
          autoComplete="off"
          maxLength={100}
        />
      )}
    </>
  );
}
